<template>
    <form v-if="loaded && !submitting" @submit.prevent="onSubmit">
        <h4 class="mb-3">
            {{ id === "new" ? "Lägg till" : "Redigera" }} integration
        </h4>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Namn</div>
            </div>
            <input type="text" class="form-control" required v-model="name" />
        </div>

        <div class="mb-3">
            <h6>Välj typ av integration</h6>

            <v-select
                v-model="type"
                :options="optionsForType"
                :reduce="(option) => option.value"
                class="mb-3"
                placeholder="Välj en"
            >
                <template #search="{ attributes, events }">
                    <input
                        class="vs__search"
                        :required="!type"
                        v-bind="attributes"
                        v-on="events"
                    />
                </template>
            </v-select>
        </div>

        <div class="mb-3" v-if="typeDefinition">
            <component
                :is="typeDefinition.component"
                v-model="config"
                :associated-quote-profiles="associatedQuoteProfiles"
            />

            <hr />
        </div>

        <div class="mb-3" v-if="quoteProfiles">
            <h6>Koppla till profiler</h6>

            <v-select
                :options="quoteProfiles"
                label="listed_name"
                :reduce="(o) => o.id"
                class="mb-3"
                placeholder="Välj en eller flera"
                multiple
                v-model="associatedQuoteProfileIds"
            >
            </v-select>
        </div>

        <div class="d-flex justify-content-between">
            <SbButton variant="secondary" @click.prevent="cancel">
                Avbryt
            </SbButton>

            <SbButton type="submit" variant="primary">
                {{ id === "new" ? "Skapa" : "Spara" }} integration
            </SbButton>
        </div>
    </form>
    <SbSpinner v-else></SbSpinner>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRefs, watch } from "vue";
import axios from "axios";
import IntegrationsFormDinteroPayments from "./form/dintero-payments";
import IntegrationsFormBriqpayPayments from "./form/briqpay-payments";
import IntegrationsFormSveaCheckoutPayments from "./form/svea-checkout-payments";
import IntegrationsFormSwishRecurringPayments from "./form/swish-recurring-payments";
import IntegrationsFormZaverPayments from "./form/zaver-payments.vue";
import IntegrationsFormKlarnaCheckoutPayments from "./form/klarna-checkout-payments.vue";
import IntegrationsFormShopifyCheckoutPayments from "./form/shopify-checkout-payments.vue";
import IntegrationsFormWoocommerceLinkPayments from "./form/woocommerce-link-payments";
import IntegrationsFormCheckoutWebhookGeneric from "./form/checkout-webhook-generic";
import IntegrationsFormCheckoutWebhookGoogleAds from "./form/checkout-webhook-google-ads";
import IntegrationsFormCheckoutWebhookGoogleAdsEnhanced from "./form/checkout-webhook-google-ads-enhanced";
import IntegrationsFormCheckoutWebhookMeta from "./form/checkout-webhook-meta";
import IntegrationsFormCheckoutWebhookMetaAdvancedMatching from "./form/checkout-webhook-meta-advanced-matching";
import IntegrationsFormCheckoutWebhookRaddningsmissionen from "./form/checkout-webhook-raddningsmissionen";
import IntegrationsFormCheckoutWebhookProfundo from "./form/checkout-webhook-profundo";
import IntegrationsFormCheckoutWebhookCallmaker from "./form/checkout-webhook-callmaker";
import IntegrationsFormCheckoutWebhookAdversus from "./form/checkout-webhook-adversus";
import IntegrationsFormCheckoutWebhookBris from "./form/checkout-webhook-bris";
import IntegrationsFormIncomingWebhookCreateBrandawareness from "./form/incoming-webhook-create-brandawareness";
import IntegrationsFormIncomingWebhookCreateQuote from "./form/incoming-webhook-create-quote";
import IntegrationsFormIncomingWebhookCreateQuoteUsingTemplate from "./form/incoming-webhook-create-quote-using-template";
import IntegrationsFormIncomingWebhookCreateQuoteFromCallmaker from "./form/incoming-webhook-create-quote-from-callmaker";
import IntegrationsFormIncomingWebhookCreateQuoteFromAdversus from "./form/incoming-webhook-create-quote-from-adversus";
import IntegrationsFormIncomingWebhookDisableAutomations from "./form/incoming-webhook-disable-automations";
import IntegrationsFormIncomingWebhookWoocommerce from "./form/incoming-webhook-woocommerce";
import IntegrationsFormCalendlyLinkQuotes from "./form/calendly-link-quotes";
import IntegrationsFormCalendlyEmbeddedCalendar from "./form/calendly-embedded-calendar";
import IntegrationsFormBillettoEmbeddedWidget from "./form/billetto-embedded-widget";
import IntegrationsFormBillectaBankAccountRetrieval from "./form/billecta-bank-account-retrieval";
import IntegrationsFormBriteAccountReport from "./form/brite-account-report.vue";
import IntegrationsFormBillectaDebtors from "./form/billecta-debtors.vue";
import IntegrationsFormFortnox from "./form/fortnox.vue";
import IntegrationsFormFtpQuotesExport from "./form/ftp-quotes-export.vue";
import IntegrationsFormHubspot from "./form/hubspot.vue";
import { Types } from "./types";

const integrationComponents = {
  "dintero-payments": IntegrationsFormDinteroPayments,
  "briqpay-payments": IntegrationsFormBriqpayPayments,
  "svea-checkout-payments": IntegrationsFormSveaCheckoutPayments,
  "swish-recurring-payments": IntegrationsFormSwishRecurringPayments,
  "zaver-payments": IntegrationsFormZaverPayments,
  "klarna-checkout-payments": IntegrationsFormKlarnaCheckoutPayments,
  "shopify-checkout-payments": IntegrationsFormShopifyCheckoutPayments,
  "woocommerce-link-payments": IntegrationsFormWoocommerceLinkPayments,
  "checkout-webhook-generic": IntegrationsFormCheckoutWebhookGeneric,
  "checkout-webhook-google-ads": IntegrationsFormCheckoutWebhookGoogleAds,
  "checkout-webhook-google-ads-enhanced": IntegrationsFormCheckoutWebhookGoogleAdsEnhanced,
  "checkout-webhook-meta": IntegrationsFormCheckoutWebhookMeta,
  "checkout-webhook-meta-advanced-matching": IntegrationsFormCheckoutWebhookMetaAdvancedMatching,
  "checkout-webhook-raddningsmissionen": IntegrationsFormCheckoutWebhookRaddningsmissionen,
  "checkout-webhook-profundo": IntegrationsFormCheckoutWebhookProfundo,
  "checkout-webhook-callmaker": IntegrationsFormCheckoutWebhookCallmaker,
  "checkout-webhook-adversus": IntegrationsFormCheckoutWebhookAdversus,
  "checkout-webhook-bris": IntegrationsFormCheckoutWebhookBris,
  "incoming-webhook-create-brandawareness": IntegrationsFormIncomingWebhookCreateBrandawareness,
  "incoming-webhook-create-quote": IntegrationsFormIncomingWebhookCreateQuote,
  "incoming-webhook-create-quote-using-template": IntegrationsFormIncomingWebhookCreateQuoteUsingTemplate,
  "incoming-webhook-create-quote-from-callmaker": IntegrationsFormIncomingWebhookCreateQuoteFromCallmaker,
  "incoming-webhook-create-quote-from-adversus": IntegrationsFormIncomingWebhookCreateQuoteFromAdversus,
  "incoming-webhook-disable-automations": IntegrationsFormIncomingWebhookDisableAutomations,
  "incoming-webhook-woocommerce": IntegrationsFormIncomingWebhookWoocommerce,
  "calendly-link-quotes": IntegrationsFormCalendlyLinkQuotes,
  "calendly-embedded-calendar": IntegrationsFormCalendlyEmbeddedCalendar,
  "billetto-embedded-widget": IntegrationsFormBillettoEmbeddedWidget,
  "billecta-bank-account-retrieval": IntegrationsFormBillectaBankAccountRetrieval,
  "brite-account-report": IntegrationsFormBriteAccountReport,
  "billecta-debtors": IntegrationsFormBillectaDebtors,
  fortnox: IntegrationsFormFortnox,
  "ftp-quotes-export": IntegrationsFormFtpQuotesExport,
  hubspot: IntegrationsFormHubspot
};

const props = defineProps({
  id: {
    type: [Number, String],
    required: true
  }
});
const {
  id
} = toRefs(props);
const emit = defineEmits(["cancelled", "created", "updated"]);
const name = ref(null);
const type = ref(null);
const config = ref({});
const loaded = ref(false);
const quoteProfiles = ref(null);
const associatedQuoteProfileIds = ref([]);
const submitting = ref(false);
const optionsForType = computed(() => {
  return Object.keys(Types).map(type => ({
    label: Types[type].label,
    value: type
  }));
});
const typeDefinition = computed(() => {
  const definition = Types[type.value];

  if (!definition) {
    return null;
  }

  return {
    ...definition,
    component: integrationComponents[type.value]
  };
});
const associatedQuoteProfiles = computed(() => {
  if (!quoteProfiles.value) {
    return [];
  }
  return quoteProfiles.value.filter(qp => associatedQuoteProfileIds.value.indexOf(qp.id) !== -1);
});
async function load() {
  if (id.value === "new") {
    name.value = null;
    type.value = null;
    config.value = {};
    associatedQuoteProfileIds.value = [];
    loaded.value = true;
    return;
  }
  const response = await axios.get(route("integrations.show", id.value));
  name.value = response.data.name;
  type.value = response.data.type;
  config.value = response.data.config;
  associatedQuoteProfileIds.value = response.data.associated_quote_profiles.map(qp => qp.id);
  loaded.value = true;
}
async function onSubmit() {
  submitting.value = true;
  const creating = id.value === "new";
  const data = {
    name: name.value,
    type: type.value,
    config: config.value,
    associated_quote_profiles: associatedQuoteProfileIds.value
  };
  try {
    let response;
    if (creating) {
      response = await axios.post(route("integrations.store"), data);
    } else {
      response = await axios.put(route("integrations.update", id.value), data);
    }
    emit(creating ? "created" : "updated", response.data);
  } catch (err) {
    console.error(err);
    throw err;
  } finally {
    submitting.value = false;
  }
}
function cancel() {
  emit("cancelled");
}
watch(id, async () => {
  loaded.value = false;
  await load();
}, {
  immediate: true
});
watch(type, (newValue, oldValue) => {
  if (!name.value || name.value == Types[oldValue]?.label) {
    name.value = Types[newValue]?.label;
  }
});
onMounted(async () => {
  const response = await axios.get(route("quoteProfiles.index"));
  quoteProfiles.value = response.data.sort((a, b) => a.name.localeCompare(b.name));
});
</script>
